import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchPaymentConfig, createPaypalOrder, capturePaypalOrder } from "../../lib/api";

// Reusable PayPal checkout for an invoice. Calls onPaid() after a verified capture.
export default function PayPalPayButton({ invoiceId, onPaid }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentConfig()
      .then(setConfig)
      .catch(() => setConfig({ configured: false }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-3" data-testid="paypal-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading secure payment…
      </div>
    );
  }

  if (!config?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex gap-3" data-testid="paypal-not-configured">
        <AlertTriangle className="h-5 w-5 flex-shrink-0" />
        <div>Online payments aren't enabled yet. Please contact our office at <span className="font-medium">252-364-3138</span> to complete payment.</div>
      </div>
    );
  }

  return (
    <div data-testid="paypal-button-wrap">
      <PayPalScriptProvider options={{ "client-id": config.client_id, currency: config.currency || "USD" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
          createOrder={async () => {
            try {
              const res = await createPaypalOrder(invoiceId);
              return res.order_id;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not start the payment.");
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              await capturePaypalOrder(invoiceId, data.orderID);
              toast.success("Payment successful!");
              onPaid?.();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment could not be completed.");
            }
          }}
          onError={() => toast.error("Something went wrong with PayPal. Please try again.")}
        />
      </PayPalScriptProvider>
      {config.environment === "sandbox" && (
        <p className="mt-3 text-[11px] text-gray-400 text-center" data-testid="paypal-sandbox-note">
          Sandbox mode — use a PayPal sandbox buyer account to test.
        </p>
      )}
    </div>
  );
}
